/**
 * Single cut position for inbound email plain text and HTML quote splitting.
 *
 * All reply-delimiter regex rules and closing-phrase handling MUST flow through this
 * module. Do not add parallel cut logic in Next.js or a second regex loop elsewhere.
 *
 * - Quote/thread patterns: earliest `match.index` from {@link REPLY_DELIMITER_REGEX_RULES}
 *   (or the UI subset without forwarding markers).
 * - Closing phrases (`inbound` and `ui`): line starts with phrase (exact, glued name, or
 *   space + more for long phrases); cut after that line plus four following lines. Skipped
 *   for `agent` so tool payloads keep signatures. Reply delimiters still hide quoted history.
 * - Earliest candidate index wins.
 *
 * Consumers:
 * - {@link buildTaskCommentDisplayPlain} / {@link buildVisiblePlainFromFull} → `comment.text` (`inbound`)
 * - HTML anchor in task-comment-email-html-trim: `inbound` for received mail, `ui` for outbound
 *   (outbound skips forwarding delimiters so user forwards stay visible in HTML)
 * - {@link buildVisiblePlainForAgentEmailComment} → agent tools (`agent`, thread regex only)
 */

import { STANDALONE_CLOSING_PHRASES } from "./email_closing_phrases";
import {
  QUOTED_LINE_RE,
  QUOTE_HEADER_LINE_RE,
  REPLY_DELIMITER_REGEX_RULES,
  UI_REPLY_QUOTE_DELIMITER_REGEX_RULES,
} from "./email_reply_delimiter_regex";
import { normalizeEmailPlainLineBreaks } from "./inbound_email_html_to_plain";

/** Lines kept starting at the first closing-phrase match (phrase line + 4 following). */
const LINES_TO_KEEP_AFTER_CLOSING_MATCH: number = 5;

function normalizeLineForClosingMatch(line: string): string {
  return line
    .trim()
    .replace(/,/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function buildClosingPhraseSet(): readonly string[] {
  const seen: Set<string> = new Set<string>();
  const normalized: string[] = [];
  for (const phrase of STANDALONE_CLOSING_PHRASES) {
    const key: string = normalizeLineForClosingMatch(phrase);
    if (key.length === 0 || seen.has(key)) continue;
    seen.add(key);
    normalized.push(key);
  }
  normalized.sort((a: string, b: string) => b.length - a.length);
  return normalized;
}

const CLOSING_PHRASES_LONGEST_FIRST: readonly string[] = buildClosingPhraseSet();

/** Min phrase length to treat "phrase + space + …" on one line as a closing (not prose). */
const CLOSING_PHRASE_PREFIX_WITH_SPACE_MIN_LEN: number = 15;

function lineStartsWithClosingPhrase(line: string): boolean {
  const normalized: string = normalizeLineForClosingMatch(line);
  if (normalized.length === 0) return false;

  for (const phrase of CLOSING_PHRASES_LONGEST_FIRST) {
    if (normalized === phrase) return true;
    if (!normalized.startsWith(phrase)) continue;

    const rest: string = normalized.slice(phrase.length);
    if (rest.length === 0) return true;
    // Outlook HTML glue: tags stripped → "GrüßenMareike" on one plain line.
    if (!rest.startsWith(" ")) return true;
    // "Mit freundlichen Grüßen Name" — not "Thanks for your email".
    if (phrase.length >= CLOSING_PHRASE_PREFIX_WITH_SPACE_MIN_LEN) return true;
  }

  return false;
}

/** Exclusive cut index after closing phrase line + four following lines. */
export function findClosingPhraseCutEndIndex(plain: string): number | null {
  if (plain.length === 0) return null;

  const lines: string[] = plain.split("\n");
  let matchIndex: number = -1;
  for (let i: number = 0; i < lines.length; i++) {
    const line: string = lines[i] ?? "";
    if (lineStartsWithClosingPhrase(line)) {
      matchIndex = i;
      break;
    }
  }
  if (matchIndex === -1) return null;

  const endExclusive: number = Math.min(
    matchIndex + LINES_TO_KEEP_AFTER_CLOSING_MATCH,
    lines.length,
  );

  return lines.slice(0, endExclusive).join("\n").length;
}

export type EmailVisiblePlainCutMode = "inbound" | "ui" | "agent";

export type ComputeEmailVisiblePlainCutOptions = {
  readonly outbound?: boolean;
};

function pickDelimiterRules(
  mode: EmailVisiblePlainCutMode,
  outbound: boolean,
): readonly RegExp[] {
  if (mode === "ui" || (mode === "agent" && outbound)) {
    return UI_REPLY_QUOTE_DELIMITER_REGEX_RULES;
  }
  return REPLY_DELIMITER_REGEX_RULES;
}

/** Lines scanned above a `>` block when looking for a quote header. */
const QUOTE_HEADER_LOOKBEHIND_LINES: number = 3;

/** True when a quote header sits within the lookbehind window above `index`. */
function hasQuoteHeaderAbove(
  lines: readonly string[],
  index: number,
): boolean {
  const start: number = Math.max(0, index - QUOTE_HEADER_LOOKBEHIND_LINES);
  for (let i: number = index - 1; i >= start; i--) {
    const previous: string = (lines[i] ?? "").trim();
    if (previous.length === 0) continue;
    if (QUOTE_HEADER_LINE_RE.test(previous)) return true;
  }
  return false;
}

/**
 * `quotedToEnd[i]` — every non-empty line from `i` onwards is `>`-quoted.
 * Precomputed in one backward pass: checking it per `>` line would be O(n²),
 * and inbound bodies can reach the 1 MiB storage cap.
 */
function buildQuotedToEndFlags(lines: readonly string[]): boolean[] {
  const flags: boolean[] = new Array<boolean>(lines.length);
  let quoted = true;
  for (let i: number = lines.length - 1; i >= 0; i--) {
    const line: string = (lines[i] ?? "").trim();
    if (line.length > 0 && !QUOTED_LINE_RE.test(line)) quoted = false;
    flags[i] = quoted;
  }
  return flags;
}

/**
 * Cut index at the line break before the first `>` line that opens quoted
 * history — either a quote header sits right above it, or the quote runs to the
 * end of the body (reply whose client wrote no header).
 *
 * Senders also indent their own paragraphs with `>`, so an unqualified `>` must
 * never truncate the message; that is what made TOP lists disappear.
 */
function findQuotedHistoryCutIndex(plain: string): number | null {
  const lines: string[] = plain.split("\n");
  const quotedToEnd: boolean[] = buildQuotedToEndFlags(lines);
  let lineStart: number = 0;
  for (let i: number = 0; i < lines.length; i++) {
    const line: string = lines[i] ?? "";
    if (
      i > 0 &&
      QUOTED_LINE_RE.test(line) &&
      (quotedToEnd[i] === true || hasQuoteHeaderAbove(lines, i))
    ) {
      return lineStart - 1;
    }
    lineStart += line.length + 1;
  }
  return null;
}

function findEarliestRegexCutIndex(
  plain: string,
  rules: readonly RegExp[],
): number | null {
  let cutIndex: number | null = null;
  for (const re of rules) {
    re.lastIndex = 0;
    const match: RegExpExecArray | null = re.exec(plain);
    if (match && typeof match.index === "number") {
      const idx: number = match.index;
      if (cutIndex === null || idx < cutIndex) cutIndex = idx;
    }
  }
  return cutIndex;
}

function computeCutIndexOnNormalizedPlain(
  plain: string,
  mode: EmailVisiblePlainCutMode,
  outbound: boolean,
): number | null {
  if (plain.length === 0) return null;

  let cutIndex: number | null = findEarliestRegexCutIndex(
    plain,
    pickDelimiterRules(mode, outbound),
  );

  const quotedHistoryIndex: number | null = findQuotedHistoryCutIndex(plain);
  if (quotedHistoryIndex !== null && quotedHistoryIndex > 0) {
    if (cutIndex === null || quotedHistoryIndex < cutIndex) {
      cutIndex = quotedHistoryIndex;
    }
  }

  // Hide long signatures/disclaimers after the closing. Agent mode keeps them.
  if (mode === "inbound" || mode === "ui") {
    const closingEnd: number | null = findClosingPhraseCutEndIndex(plain);
    if (closingEnd !== null && closingEnd > 0) {
      if (cutIndex === null || closingEnd < cutIndex) cutIndex = closingEnd;
    }
  }

  if (cutIndex === null || cutIndex <= 0) return null;
  return cutIndex;
}

/**
 * Byte index in normalized plain text where visible content ends (exclusive).
 * Returns null when nothing should be cut.
 */
export function computeEmailVisiblePlainCut(
  fullPlain: string,
  mode: EmailVisiblePlainCutMode = "inbound",
  options?: ComputeEmailVisiblePlainCutOptions,
): number | null {
  const plain: string = normalizeEmailPlainLineBreaks(fullPlain.trimEnd());
  const outbound: boolean = options?.outbound === true;
  return computeCutIndexOnNormalizedPlain(plain, mode, outbound);
}

function buildVisiblePlainFromNormalizedCut(
  plain: string,
  cutIndex: number | null,
): string {
  if (plain.length === 0) return "";
  if (cutIndex === null) return plain.trim();

  const head: string = plain.slice(0, cutIndex).trimEnd();
  if (head.length === 0) return plain.trim();
  return head;
}

/** Agent tools: thread regex only — keeps closing phrases / signatures. */
export function buildVisiblePlainForAgentEmailComment(
  fullPlain: string,
  outbound: boolean,
): string {
  const plain: string = normalizeEmailPlainLineBreaks(fullPlain.trimEnd());
  const cutIndex: number | null = computeCutIndexOnNormalizedPlain(
    plain,
    "agent",
    outbound,
  );
  return buildVisiblePlainFromNormalizedCut(plain, cutIndex);
}

/** Visible plain for task comments — same cut as HTML anchor uses on `full_plain`. */
export function buildVisiblePlainFromFull(fullPlain: string): string {
  const plain: string = normalizeEmailPlainLineBreaks(fullPlain.trimEnd());
  const cutIndex: number | null = computeCutIndexOnNormalizedPlain(
    plain,
    "inbound",
    false,
  );
  return buildVisiblePlainFromNormalizedCut(plain, cutIndex);
}
